import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/token/tokenService.js

/**
 * Token Service
 * 
 * Handles invitation token lifecycle including validation, storage, and retrieval.
 * Provides fallback mechanisms for session persistence and cross-device synchronization.
 * 
 * Features:
 * - Token validation and inspection
 * - Multi-storage strategy (localStorage + sessionStorage)
 * - Time-based expiration
 * - Development mode testing support
 * - Detailed token metadata tracking
 */

/**
 * Process an invitation token and determine the appropriate action
 * Validates token against API and returns navigation and data instructions
 * 
 * @param {string} token The invitation token from URL
 * @returns {Object} Result object with status, redirect, data, and message
 */
export const processInvitationToken = async (token) => {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('Processing invitation token:', token);
    }
    
    // Development fallback for testing - bypass API call
    if (process.env.NODE_ENV === 'development') {
      // Test case for valid invitation
      if (token === '3291ca38-c09e-438c-b721-33b914ff7acb' || token === 'test-valid') {
        console.log('Using test token for valid invitation');
        
        return { 
          status: 'VALID', 
          redirect: '/register', 
          data: {
            TokenStatus: "Valid invitation",
            RequesterCompanyName: "Test Company",
            RequesterContactEmail: "contact@testcompany.com",
            SupplierName: "Test Supplier",
            SupplierId: "SUP-12345",
            SupplierEmailAnonymous: "test***@testsupplier.com",
            RequestedSector: "Manufacturing",
            RequestedCountry: "DE",
            SupplierEmailDomainRegistered: false,
            LinkingId: 12345,
            InvitationToken: token
          }
        };
      }
      
      // Test case for valid invitation with domain registered
      if (token === 'test-domain') {
        console.log('Using test token for valid invitation with domain registered');
        
        return { 
          status: 'VALID', 
          redirect: '/register', 
          data: {
            TokenStatus: "Valid invitation",
            RequesterCompanyName: "Test Company",
            RequesterContactEmail: "contact@testcompany.com",
            SupplierName: "Test Supplier",
            SupplierId: "SUP-12345",
            SupplierEmailAnonymous: "test***@testsupplier.com",
            RequestedSector: "Manufacturing",
            RequestedCountry: "DE",
            SupplierEmailDomainRegistered: true,
            LinkingId: 12346,
            InvitationToken: token
          }
        };
      }
      
      // Test case for completed registration
      if (token === 'test-progress' || token === 'test-complete') {
        console.log('Using test token for registration completed');
        
        return { 
          status: 'ALREADY_REGISTERED', 
          redirect: '/', 
          data: {
            TokenStatus: "Error: token used, registration completed",
            RequesterCompanyName: "Test Company",
            RequesterContactEmail: "contact@testcompany.com",
            SupplierName: "Test Supplier",
            SupplierId: "SUP-12345",
            SupplierEmailAnonymous: "test***@testsupplier.com",
            RequestedSector: "Manufacturing",
            RequestedCountry: "DE",
            LinkingId: 12347,
            InvitationToken: token
          },
          message: 'Your company is already registered. Please login with your credentials.'
        };
      }
    }
    
    // Call API to validate token
    const response = await fetch(
      `${process.env.GATSBY_STRAPI_URL}/api/landing/validate-token`,
      {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getStrapiToken()}`
        },
        body: JSON.stringify({ token })
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Failed to validate token');
    }
    
    const data = await response.json();
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Token validation response:', data);
    }
    
    // Route based on token status
    switch(data.TokenStatus) {
      case "Valid invitation":
        return { 
          status: 'VALID', 
          redirect: '/register', 
          data 
        };
        
      case "Error: token used, registration completed":
      case "Error: token used, registration in progress":
        return { 
          status: 'ALREADY_REGISTERED', 
          redirect: '/', 
          data,
          message: 'Your company is already registered. Please login with your credentials.'
        };
        
      case "Error: token invalid":
      default:
        return { 
          status: 'INVALID', 
          redirect: '/error', 
          message: 'The invitation token is invalid or has expired.',
          data
        };
    }
  } catch (error) {
    console.error('Token processing error:', error);
    return { 
      status: 'ERROR', 
      redirect: '/error', 
      message: error.message || 'Failed to process invitation token' 
    };
  }
};

/**
 * Store token data in both sessionStorage and localStorage for redundancy
 * Provides cross-tab synchronization and persistence across page loads
 * 
 * @param {Object} tokenData The token data to store
 */
export const storeTokenData = (tokenData) => {
  if (!tokenData) return;
  
  try {
    const serializedData = JSON.stringify(tokenData);
    
    // Store in both for maximum reliability
    sessionStorage.setItem('tokenData', serializedData);
    localStorage.setItem('tokenData', serializedData);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Token data stored in storage', {
        tokenPresent: !!tokenData.InvitationToken,
        linkingId: tokenData.LinkingId,
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Error storing token data:', error);
  }
};

/**
 * Retrieve token data from storage with fallbacks
 * Uses multi-layer storage strategy with automatic recovery
 * 
 * @returns {Object|null} The stored token data, or null if none
 */
export const getStoredTokenData = () => {
  try {
    // Try sessionStorage first then fallback to localStorage
    let data = sessionStorage.getItem('tokenData');
    if (!data) {
      data = localStorage.getItem('tokenData');
    }
    
    if (data) {
      const parsedData = JSON.parse(data);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Retrieved token data from storage', {
          tokenPresent: !!parsedData.InvitationToken,
          linkingId: parsedData.LinkingId
        });
      }
      
      return parsedData;
    }
    
    return null;
  } catch (error) {
    console.error('Error retrieving token data from storage:', error);
    return null;
  }
};

/**
 * Clear token data from all storage mechanisms
 * Ensures complete removal from all persistence layers
 */
export const clearTokenData = () => {
  try {
    sessionStorage.removeItem('tokenData');
    localStorage.removeItem('tokenData');
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Token data cleared from storage');
    }
  } catch (error) {
    console.error('Error clearing token data:', error);
  }
};

/**
 * Store LinkingId for invitation acceptance after login
 * Uses both localStorage and sessionStorage with expiry time
 * 
 * @param {number} linkingId The LinkingId to store
 * @param {Object} tokenData Associated token data
 */
export const storePendingAcceptance = (linkingId, tokenData) => {
  if (!linkingId) return;
  
  try {
    // Store the linking ID and associated token data
    const pendingAcceptance = {
      linkingId: linkingId,
      tokenData: tokenData,
      timestamp: Date.now(),
      expires: Date.now() + (30 * 60 * 1000) // 30 minute expiry
    };
    
    const serialized = JSON.stringify(pendingAcceptance);
    
    // Store in both localStorage and sessionStorage for redundancy
    localStorage.setItem('pendingAcceptance', serialized);
    sessionStorage.setItem('pendingAcceptance', serialized);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Pending acceptance stored successfully:', {
        linkingId,
        requester: tokenData?.RequesterCompanyName || 'Unknown',
        timestamp: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Error storing pending acceptance:', error);
  }
};

/**
 * Get stored pending acceptance data with expiry check
 * Tries both localStorage and sessionStorage with automatic expiry handling
 * 
 * @returns {Object|null} The pending acceptance data or null if none/expired
 */
export const getPendingAcceptance = () => {
  try {
    // Try localStorage first, then sessionStorage
    let data = localStorage.getItem('pendingAcceptance');
    if (!data) {
      data = sessionStorage.getItem('pendingAcceptance');
    }
    
    if (!data) {
      if (process.env.NODE_ENV === 'development') {
        console.log('No pending acceptance data found in storage');
      }
      return null;
    }
    
    const pendingAcceptance = JSON.parse(data);
    
    // Check if data has expired
    if (pendingAcceptance.expires && pendingAcceptance.expires < Date.now()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Pending acceptance has expired, clearing data');
      }
      clearPendingAcceptance();
      return null;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Retrieved pending acceptance from storage:', {
        linkingId: pendingAcceptance.linkingId,
        requester: pendingAcceptance.tokenData?.RequesterCompanyName || 'Unknown',
        age: Math.round((Date.now() - pendingAcceptance.timestamp) / 1000) + 's'
      });
    }
    
    return pendingAcceptance;
  } catch (error) {
    console.error('Error retrieving pending acceptance:', error);
    return null;
  }
};

/**
 * Clear pending acceptance data from all storage
 * Ensures complete removal from all persistence layers
 */
export const clearPendingAcceptance = () => {
  try {
    localStorage.removeItem('pendingAcceptance');
    sessionStorage.removeItem('pendingAcceptance');
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Pending acceptance cleared from storage');
    }
  } catch (error) {
    console.error('Error clearing pending acceptance:', error);
  }
};